import { Box, Text } from '@chakra-ui/react';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { IClientApiResponse } from '~/clientApi/clientApi.types';
import { Creator } from '~/clientApi/creator.model';
import { TalentRosterEmptyResults } from '~/components/TalentRosterEmptyResults';
import { StackViewCreator } from '~/screens/MyAgency/components/StackView/StackViewCreator';
import {
  useAdvancedFiltersStore,
  useCreatorsFiltersStore,
  useSavedListsStore,
  useSearchTalentStore,
} from '~/screens/MyAgency/store';
import { SearchCreatorsApiResponse } from '~/searchApi';
import { ICreator } from '~/types';

import { InviteCreatorEmptyPage } from './InviteCreatorEmptyPage';
import { StackCreatorSkeletons } from './StackCreatorSkeletons';

interface StackViewProps {
  agencyData: ICreator[];
  error: unknown;
  hasNextPage?: boolean;
  isInitialFetch: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isShared: boolean;
  withContent?: boolean;
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<
    InfiniteQueryObserverResult<
      IClientApiResponse<Creator> | SearchCreatorsApiResponse | undefined,
      unknown
    >
  >;
}

export const StackView: React.FC<StackViewProps> = ({
  agencyData,
  error,
  hasNextPage,
  isInitialFetch,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  isShared,
  withContent = true,
}) => {
  const { appliedFilters: advancedFilters } = useAdvancedFiltersStore();
  const { appliedFilters: creatorsFilters } = useCreatorsFiltersStore();
  const appliedFilters = [...advancedFilters, ...creatorsFilters];
  const { creators: storedCreators } = useSavedListsStore();
  const { query } = useSearchTalentStore();

  useEffect(() => {
    if (hasNextPage === true && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isInitialFetch) return <StackCreatorSkeletons />;

  if (error) return <Text color="error.500">Something went wrong.</Text>;

  if (isLoading) return <StackCreatorSkeletons />;

  return (
    <Box>
      {(appliedFilters.length > 0 && agencyData.length === 0) ||
      (agencyData.length === 0 && query.length > 0) ? (
        <TalentRosterEmptyResults />
      ) : agencyData.length === 0 ? (
        <InviteCreatorEmptyPage />
      ) : (
        <Virtuoso
          useWindowScroll
          totalCount={agencyData.length}
          itemContent={(index) => (
            <StackViewCreator
              creator={agencyData[index]}
              isShared={isShared}
              isChecked={
                !!storedCreators.find((c) => c.id === agencyData[index].id)
              }
              withContent={withContent}
            />
          )}
        />
      )}
    </Box>
  );
};
